/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Collapse,
    CircularProgress,
    Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { error as errorPalette, primary, warning } from 'src/theme/palette';

// -----------------------------------------------

function parseDurationSecs(d) {
    if (!d) return null;
    if (typeof d === 'number') return d;
    if (typeof d !== 'string') return null;
    const parts = d.replace(' ', ':').split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 4) return parts[0] * 86400 + parts[1] * 3600 + parts[2] * 60 + parts[3];
    return null;
}

function formatSeconds(totalSeconds) {
    if (!totalSeconds || totalSeconds < 0) return '00:00:00';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function formatEntry(entry) {
    const start = entry.started_at
        ? new Date(entry.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '—';
    const end = entry.stopped_at
        ? new Date(entry.stopped_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'running';
    const dur = entry.duration_seconds ? formatSeconds(entry.duration_seconds) : '—';
    return `${start} → ${end} (${dur})`;
}

function parseTimestampMs(value) {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const timestampMs = new Date(value).getTime();
    return Number.isFinite(timestampMs) ? timestampMs : null;
}

function getActiveTimerStartMs(task, timerOverride) {
    if (timerOverride?.stopped || task?.actual_end) return null;

    const overrideStartMs = parseTimestampMs(timerOverride?.actualStart);
    if (overrideStartMs !== null) return overrideStartMs;

    const activeEntry = (task?.time_entries || []).find(
        (entry) => !entry?.stopped_at && parseTimestampMs(entry?.started_at) !== null
    );
    if (activeEntry) return parseTimestampMs(activeEntry.started_at);

    return parseTimestampMs(task?.actual_start)
        ?? parseTimestampMs(task?.active_timer_started_at);
}

function getRecordedTimerSeconds(task) {
    const serializedTotal = task?.total_time_seconds;
    if (serializedTotal !== null && serializedTotal !== undefined && serializedTotal !== '') {
        const totalSeconds = Number(serializedTotal);
        if (Number.isFinite(totalSeconds) && totalSeconds >= 0) return totalSeconds;
    }

    const actualStartMs = parseTimestampMs(task?.actual_start);
    const actualEndMs = parseTimestampMs(task?.actual_end);
    if (actualStartMs === null || actualEndMs === null) return 0;

    return Math.max(0, Math.floor((actualEndMs - actualStartMs) / 1000));
}

// -----------------------------------------------

export default function TaskTimeTracker({ practiceId, taskId, task = null, onRefresh = null }) {
    const [timerLoading, setTimerLoading] = useState(false);
    const [liveSeconds, setLiveSeconds] = useState(0);
    const [entriesOpen, setEntriesOpen] = useState(false);
    const [timerOverride, setTimerOverride] = useState(null);
    const intervalRef = useRef(null);
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    const timerLoadingRef = useRef(false);

    const taskScope = `${practiceId}:${taskId}`;
    const taskScopeRef = useRef(taskScope);
    taskScopeRef.current = taskScope;

    const setTimerBusy = (value) => {
        timerLoadingRef.current = value;
        setTimerLoading(value);
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            timerLoadingRef.current = false;
            requestIdRef.current += 1;
        };
    }, []);

    useEffect(() => {
        requestIdRef.current += 1;
        setTimerBusy(false);
        setEntriesOpen(false);
    }, [taskScope]);

    const timeEntries = task?.time_entries || [];
    const scopedOverride = timerOverride?.taskScope === taskScope ? timerOverride : null;
    const totalSeconds = scopedOverride?.durationSeconds ?? getRecordedTimerSeconds(task);

    const activeTimerStartMs = getActiveTimerStartMs(task, scopedOverride);
    const isRunning = activeTimerStartMs !== null;
    const hasPersistedStart = parseTimestampMs(task?.actual_start) !== null;
    const hasStoppedTimer = Boolean(scopedOverride?.stopped || task?.actual_end);
    const canStartTimer = !isRunning && !hasPersistedStart && !hasStoppedTimer;

    // Live tick when running
    useEffect(() => {
        if (isRunning) {
            const tick = () => {
                const elapsed = Math.max(0, Math.floor((Date.now() - activeTimerStartMs) / 1000));
                setLiveSeconds(elapsed);
            };
            tick();
            intervalRef.current = setInterval(tick, 1000);
        } else {
            setLiveSeconds(0);
            if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isRunning, activeTimerStartMs]);

    const isCurrentRequest = (requestId, requestScope) => (
        mountedRef.current
        && requestIdRef.current === requestId
        && taskScopeRef.current === requestScope
    );

    const shouldIgnorePointerActivation = (event) => (
        event?.type === 'pointerup'
        && event.pointerType === 'mouse'
        && (event.button ?? 0) !== 0
    );

    const handleStart = async (event = null) => {
        event?.preventDefault?.();
        if (timerLoadingRef.current || shouldIgnorePointerActivation(event)) return;
        const requestScope = taskScope;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setTimerBusy(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/start-timer/`,
                'post',
                {}
            );
            if (!isCurrentRequest(requestId, requestScope)) return;
            if (res.status === 200 || res.status === 201) {
                const actualStart = res.data?.actual_start;
                if (parseTimestampMs(actualStart) !== null) {
                    setTimerOverride({ taskScope: requestScope, actualStart, stopped: false });
                }
                if (onRefresh) onRefresh();
            } else {
                console.warn('[TaskTimeTracker] Start timer failed:', res.status, res.data);
                const msg = res.data?.detail || 'Failed to start timer.';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            if (!isCurrentRequest(requestId, requestScope)) return;
            Sentry.captureException(err, { tags: { context: 'task_timer_start' } });
            dispatch(updateError({ success: false, message: 'Failed to start timer.' }));
        } finally {
            if (isCurrentRequest(requestId, requestScope)) setTimerBusy(false);
        }
    };

    const handleStop = async (event = null) => {
        event?.preventDefault?.();
        if (timerLoadingRef.current || shouldIgnorePointerActivation(event)) return;
        const requestScope = taskScope;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setTimerBusy(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/stop-timer/`,
                'post',
                {}
            );
            if (!isCurrentRequest(requestId, requestScope)) return;
            if (res.status === 200 || res.status === 201) {
                const durationSeconds = Number(res.data?.duration_seconds);
                setTimerOverride({
                    taskScope: requestScope,
                    stopped: true,
                    durationSeconds: Number.isFinite(durationSeconds) && durationSeconds >= 0
                        ? durationSeconds
                        : totalSeconds + liveSeconds,
                });
                if (onRefresh) onRefresh();
            } else {
                console.warn('[TaskTimeTracker] Stop timer failed:', res.status, res.data);
                const msg = res.data?.detail || 'Failed to stop timer.';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            if (!isCurrentRequest(requestId, requestScope)) return;
            Sentry.captureException(err, { tags: { context: 'task_timer_stop' } });
            dispatch(updateError({ success: false, message: 'Failed to stop timer.' }));
        } finally {
            if (isCurrentRequest(requestId, requestScope)) setTimerBusy(false);
        }
    };

    const completedEntries = timeEntries.filter((e) => e.stopped_at);
    const displaySeconds = isRunning ? totalSeconds + liveSeconds : totalSeconds;

    const targetSecs = parseDurationSecs(task?.target_duration);
    let slaState = null;
    if (targetSecs) {
        if (displaySeconds >= targetSecs) slaState = 'exceeded';
        else if (displaySeconds >= targetSecs * 0.8) slaState = 'near';
        else slaState = 'on_track';
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
                {/* Clock icon */}
                <Iconify
                    icon="eva:clock-fill"
                    width={16}
                    sx={{ color: isRunning ? errorPalette.main : 'text.disabled', flexShrink: 0 }}
                />

                {/* Time display */}
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: isRunning ? errorPalette.main : 'text.primary',
                        minWidth: 72,
                    }}
                >
                    {formatSeconds(displaySeconds)}
                </Typography>

                {/* SLA chip */}
                {slaState === 'exceeded' && (
                    <Chip
                        size="small"
                        label="SLA exceeded"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: errorPalette.main, bgcolor: alpha(errorPalette.main, 0.08) }}
                    />
                )}
                {slaState === 'near' && (
                    <Chip
                        size="small"
                        label="Near limit"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: warning.main, bgcolor: alpha(warning.main, 0.08) }}
                    />
                )}
                {slaState === 'on_track' && (
                    <Chip
                        size="small"
                        label="On track"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, borderRadius: 1, color: primary.main, bgcolor: alpha(primary.main, 0.08) }}
                    />
                )}

                {/* Start / Stop button */}
                {isRunning && (
                    <Button
                        type="button"
                        size="small"
                        variant="contained"
                        disabled={timerLoading}
                        onClick={handleStop}
                        onPointerUp={handleStop}
                        startIcon={timerLoading ? <CircularProgress size={12} color="inherit" /> : <Iconify icon="eva:stop-circle-fill" width={14} />}
                        sx={{
                            bgcolor: errorPalette.main,
                            '&:hover': { bgcolor: errorPalette.dark },
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            fontWeight: 700,
                        }}
                    >
                        {timerLoading ? 'Stopping…' : 'Stop'}
                    </Button>
                )}
                {!isRunning && canStartTimer && (
                    <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        disabled={timerLoading}
                        onClick={handleStart}
                        onPointerUp={handleStart}
                        startIcon={timerLoading ? <CircularProgress size={12} color="inherit" /> : <Iconify icon="eva:play-circle-fill" width={14} />}
                        sx={{
                            borderColor: primary.main,
                            color: primary.main,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            fontWeight: 600,
                            '&:hover': { bgcolor: alpha(primary.main, 0.06) },
                        }}
                    >
                        {timerLoading ? 'Starting…' : 'Start timer'}
                    </Button>
                )}

                {/* View entries toggle */}
                {completedEntries.length > 0 && (
                    <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => setEntriesOpen(!entriesOpen)}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', p: 0 }}
                    >
                        {entriesOpen ? 'Hide entries' : `View entries (${completedEntries.length})`}
                    </Button>
                )}
            </Stack>

            {/* Entry list */}
            <Collapse in={entriesOpen && completedEntries.length > 0}>
                <Box sx={{ mt: 1, pl: 3.5 }}>
                    {completedEntries.map((entry, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mb: 0.25 }}>
                            {formatEntry(entry)}
                        </Typography>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

TaskTimeTracker.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    task: PropTypes.shape({
        total_time_seconds: PropTypes.number,
        time_entries: PropTypes.arrayOf(PropTypes.shape({
            started_at: PropTypes.string,
            stopped_at: PropTypes.string,
            duration_seconds: PropTypes.number,
        })),
        active_timer_started_at: PropTypes.string,
        actual_start: PropTypes.string,
        actual_end: PropTypes.string,
        target_duration: PropTypes.string,
    }),
    onRefresh: PropTypes.func,
};
